/**
 * Trustpilot Integration Module (Story 23.3)
 *
 * Barrel export + side-effect adapter registration. Registration happens
 * here — NOT in `trustpilot.ts` — to prevent the Failure 5a anti-pattern
 * documented in `google/index.ts`. Importing this module is sufficient
 * to register the adapter; importing `trustpilot.ts` directly would give
 * you the adapter object without registration.
 */

import { registerAdapter } from "../registry";
import { trustpilotAdapter } from "./trustpilot";

// Side-effect registration (Story 23.3)
registerAdapter("trustpilot", trustpilotAdapter);

// Public surface — adapter + canonical constants
export {
  trustpilotAdapter,
  TRUSTPILOT_PLATFORM_VALUES,
  TRUSTPILOT_API_VERSION,
  TRUSTPILOT_API_BASE,
  // Story 35.3 — second base URL re-exported for downstream consumers.
  TRUSTPILOT_INVITATIONS_API_BASE,
  type TrustpilotPlatform,
} from "./trustpilot";

// Metrics + dimensions
export {
  TRUSTPILOT_METRICS,
  TRUSTPILOT_DIMENSIONS,
} from "./metric-allowlist";

// OAuth — client_credentials token exchange
export {
  exchangeTrustpilotClientCredentials,
  buildTrustpilotBasicAuthHeader,
  TRUSTPILOT_OAUTH_TOKEN_URL,
  type TrustpilotClientCredentialsResult,
} from "./oauth";

// API helpers
export {
  findTrustpilotBusinessUnitByDomain,
  fetchTrustpilotBusinessUnitDetails,
  fetchTrustpilotPublicReviews,
  fetchTrustpilotPrivateReviews,
  mergeTrustpilotPublicAndPrivateReviews,
  // Story 35.3 — Invitations API + paginated reviews helpers.
  fetchTrustpilotInvitationData,
  fetchTrustpilotPaginatedReviews,
  TRUSTPILOT_MAX_REVIEW_PAGES,
  type TrustpilotBusinessUnit,
  type TrustpilotFindBusinessUnitResult,
  type TrustpilotPublicReviewsResult,
  type TrustpilotBusinessUnitDetails,
  type TrustpilotInvitationData,
  type TrustpilotPaginatedReviewsResult,
} from "./api";

// Story 35.3 — Invitations API retention pre-flight helper.
export {
  TRUSTPILOT_INVITATIONS_RETENTION_DAYS,
  computeTrustpilotInvitationsRetentionCutoff,
  validateTrustpilotInvitationsDateRange,
  type TrustpilotInvitationsRetentionResult,
} from "./retention";

// Errors
export {
  parseTrustpilotApiError,
  sanitizeTrustpilotErrorMessage,
  buildTrustpilotAdapterError,
  TRUSTPILOT_429_COOLDOWN_MS,
  TrustpilotApiCallError,
  type TrustpilotApiError,
} from "./errors";

// Safe fetch
export {
  safeFetchTrustpilot,
  type TrustpilotSafeFetchContext,
} from "./safe-fetch";

// PII
export {
  TRUSTPILOT_PII_FIELDS,
  TRUSTPILOT_PRIVATE_FIELDS,
  sanitizeTrustpilotPii,
  stripCsvInjectionPrefix,
} from "./pii";

// Transformers
export {
  buildTrustpilotReviewDistribution,
  buildTrustpilotReviewSummary,
  transformTrustpilotBusinessUnitDetails,
  transformTrustpilotReview,
  // Story 35.3 Batch 2 — Invitations API transformer.
  transformTrustpilotInvitationData,
  trustpilotSentimentBucket,
  type TrustpilotReportType,
} from "./transformers";

// Fields (per-endpoint allowlist for build-time test)
export {
  TRUSTPILOT_BUSINESS_UNIT_FIELDS,
  TRUSTPILOT_SERVICE_REVIEW_FIELDS,
  TRUSTPILOT_PRIVATE_REVIEW_FIELDS,
  TRUSTPILOT_INVITATIONS_FIELDS,
  TRUSTPILOT_DERIVED_FIELDS,
  allTrustpilotFields,
} from "./fields";

// Cache policy
export {
  TRUSTPILOT_CACHE_TTL_SEC,
  TRUSTPILOT_HAS_RETENTION_PREFLIGHT,
} from "./cache-policy";

// Validate connection — re-export the adapter method as a free function
// for the cron healthCheck handler.
import { trustpilotAdapter as _trustpilotAdapterInternal } from "./trustpilot";
export const validateTrustpilotConnection =
  _trustpilotAdapterInternal.validateConnection.bind(_trustpilotAdapterInternal);
